import { valibotResolver } from "@hookform/resolvers/valibot";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import {
  DialogClose,
  DialogDescription,
  DialogTitle,
} from "common/components/ui/Dialog";
import {
  DrawerClose,
  DrawerDescription,
  DrawerTitle,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import useWorkspaceMutations from "common/datahooks/useWorkspaceMutations";
import useWorkspaces from "common/datahooks/useWorkspaces";
import useGTM from "common/hooks/useGTM";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import { WorkspaceNameSchema } from "common/schemas";

import CreateWorkspaceImage from "assets/images/create-workspace.webp";

export default function CreateWorkspaces({ isDialog }: { isDialog: boolean }) {
  const isDesktop = useTwBreakpoint("lg");

  const { sendEvent } = useGTM();
  const navigate = useNavigate();
  const { createWorkspace, isCreatingWorkspace } = useWorkspaceMutations();
  const { workspaces } = useWorkspaces();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
    },
    resolver: valibotResolver(WorkspaceNameSchema),
  });

  function onSubmit({ name }: { name: string }) {
    if (workspaces.find((workspace) => workspace.name === name.trim())) {
      setError("name", { message: "Workspace with this name already exists" });
    } else {
      createWorkspace({ name }).then((workspace) => {
        sendEvent("workspace_created");
        navigate(`/workspaces/${workspace.id}`);
      });
    }
  }

  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;
  const Close = isDesktop ? DialogClose : DrawerClose;

  const titleText = "Create a new workspace";
  const descriptionText =
    "This will be the main place to manage your Aimfox campaigns and collaborate with your team";

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <img
        src={CreateWorkspaceImage}
        alt="Workspace Creation"
        height={isDialog ? 195 : 254}
        className="mx-auto"
      />

      {isDialog ? (
        <Title>{titleText}</Title>
      ) : (
        <h1 className="text-xl-bold text-center mb-2 mt-4">{titleText}</h1>
      )}

      {isDialog ? (
        <Description className="mb-8">{descriptionText}</Description>
      ) : (
        <p className="text-center text-sm text-black-500 mb-6">
          {descriptionText}
        </p>
      )}

      <FormField
        htmlFor="workspaceName"
        label="Workspace name"
        className="mb-6"
        error={errors.name?.message}
        size={isDesktop ? "md" : "lg"}
      >
        <Input
          variant={isDesktop ? "md" : "lg"}
          id="workspaceName"
          placeholder="Name your workspace"
          {...register("name")}
        />
      </FormField>

      <div className="flex justify-between gap-x-2.5">
        <RenderIf condition={isDialog}>
          <Close asChild>
            <Button variant="secondary-black">Cancel</Button>
          </Close>
        </RenderIf>

        <Button
          className="ml-auto"
          type="submit"
          isLoading={isCreatingWorkspace}
        >
          Create
        </Button>
      </div>
    </form>
  );
}
